import _ from 'lodash';

import {
  ContactTypeExternalProvider,
  ContactTypeKind,
  DriveCoverageStatus,
  DriveMappingState,
  MIN_SLOTS_TO_COLLAPSE,
} from 'constants/contactTypes';
import { getConditionalArray, getConditionalObject, prepareStringForComparison } from 'utils/data';

const NUMBERED_SLOT_PATTERN = /^(.*\S)\s+(\d+)$/;

export const getDriveMapping = contactType =>
  contactType?.mappings?.find(
    ({ provider }) => provider === ContactTypeExternalProvider.DATA_VERIFY_DRIVE,
  ) ?? null;

const getDriveMappingState = contactType => {
  const mapping = getDriveMapping(contactType);

  if (!mapping) {
    return DriveMappingState.NOT_MAPPED;
  }

  if (!mapping.isEnabled) {
    return DriveMappingState.NOT_SENT;
  }

  return mapping.personCode || mapping.companyCode
    ? DriveMappingState.MAPPED
    : DriveMappingState.NOT_MAPPED;
};

export const getIsDriveMapped = contactType =>
  getDriveMappingState(contactType) === DriveMappingState.MAPPED;

export const getIsPersonCodeAllowed = kind =>
  kind === ContactTypeKind.PERSON || kind === ContactTypeKind.PERSON_AND_COMPANY;

export const getIsCompanyCodeAllowed = kind =>
  kind === ContactTypeKind.COMPANY || kind === ContactTypeKind.PERSON_AND_COMPANY;

export const getDriveFamiliesForKind = (families, familyKind) =>
  families.filter(({ kind }) => kind === familyKind);

export const findDriveFamily = (families, code) => {
  if (!code) {
    return null;
  }

  const preparedCode = prepareStringForComparison(code);

  return families.find(family => prepareStringForComparison(family.code) === preparedCode) ?? null;
};

const getNumberedSlot = slot => {
  const match = NUMBERED_SLOT_PATTERN.exec(slot);

  return match ? { prefix: match[1], number: Number(match[2]) } : null;
};

const getSlotRuns = slots =>
  slots.reduce((runs, slot) => {
    const numberedSlot = getNumberedSlot(slot);
    const lastRun = _.last(runs);
    const isRunContinued =
      !!numberedSlot &&
      !!lastRun?.prefix &&
      lastRun.prefix === numberedSlot.prefix &&
      lastRun.lastNumber + 1 === numberedSlot.number;

    if (isRunContinued) {
      lastRun.slots.push(slot);
      lastRun.lastNumber = numberedSlot.number;

      return runs;
    }

    runs.push({
      prefix: numberedSlot?.prefix ?? null,
      firstNumber: numberedSlot?.number ?? null,
      lastNumber: numberedSlot?.number ?? null,
      slots: [slot],
    });

    return runs;
  }, []);

export const getSlotSummary = (slots = []) =>
  getSlotRuns(slots)
    .flatMap(run =>
      run.slots.length >= MIN_SLOTS_TO_COLLAPSE
        ? [`${run.prefix} ${run.firstNumber}–${run.lastNumber}`]
        : run.slots,
    )
    .join(', ');

export const getSlotBehaviourText = family => {
  const slots = family?.slots ?? [];

  if (slots.length < 2) {
    return null;
  }

  return `${family.code} → ${getSlotSummary(slots)}, assigned in order. Parties beyond ${
    slots.length
  } are logged on the DRIVE request and not sent.`;
};

export const getIsContactTypeEditable = (contactType, { isEditAllowed, isInternalCompany }) =>
  isEditAllowed && (contactType.isSystem || !contactType.isGlobal || isInternalCompany);

export const getIsContactTypeDeletable = (contactType, { isManageAllowed, isInternalCompany }) =>
  isManageAllowed && !contactType.isSystem && (!contactType.isGlobal || isInternalCompany);

export const getIsDriveMappingResettable = (contactType, { isEditAllowed, isInternalCompany }) =>
  isEditAllowed &&
  !isInternalCompany &&
  !!contactType.isSystem &&
  !!getDriveMapping(contactType)?.isOverride;

export const filterContactTypes = (contactTypes, { searchText, isUnmappedOnly }) => {
  const preparedSearchText = prepareStringForComparison(searchText);

  return contactTypes.filter(contactType => {
    if (isUnmappedOnly && getIsDriveMapped(contactType)) {
      return false;
    }

    if (!preparedSearchText) {
      return true;
    }

    const mapping = getDriveMapping(contactType);

    return [contactType.name, mapping?.personCode, mapping?.companyCode].some(
      value => !!prepareStringForComparison(value)?.includes(preparedSearchText),
    );
  });
};

export const splitContactTypesBySystem = contactTypes => {
  const [systemContactTypes, companyContactTypes] = _.partition(
    contactTypes,
    ({ isSystem }) => !!isSystem,
  );

  return {
    systemContactTypes: _.orderBy(systemContactTypes, ['sortOrder', 'name']),
    companyContactTypes: _.orderBy(companyContactTypes, [
      ({ name }) => prepareStringForComparison(name),
    ]),
  };
};

const getMappedFamilyCodes = contactType => {
  const mapping = getDriveMapping(contactType);

  return [mapping?.personCode, mapping?.companyCode]
    .filter(Boolean)
    .map(code => prepareStringForComparison(code));
};

const getCoverageStatus = sources => {
  if (!sources.length) {
    return DriveCoverageStatus.NO_SOURCE;
  }

  return sources.some(({ isSent }) => isSent)
    ? DriveCoverageStatus.MAPPED
    : DriveCoverageStatus.NOT_SENT;
};

export const getDriveCoverageRows = (families, contactTypes) =>
  families.map(family => {
    const familyCode = prepareStringForComparison(family.code);

    const sources = contactTypes
      .filter(contactType => getMappedFamilyCodes(contactType).includes(familyCode))
      .map(contactType => ({
        id: contactType.id,
        name: contactType.name,
        isSystem: !!contactType.isSystem,
        isSent: !!getDriveMapping(contactType)?.isEnabled,
      }));

    return {
      ...family,
      id: family.code,
      sources,
      status: getCoverageStatus(sources),
    };
  });

export const filterDriveCoverageRows = (rows, { searchText, status }) => {
  const preparedSearchText = prepareStringForComparison(searchText);

  return rows.filter(row => {
    if (status && row.status !== status) {
      return false;
    }

    if (!preparedSearchText) {
      return true;
    }

    return [row.code, row.group, ...row.slots, ...row.sources.map(({ name }) => name)].some(
      value => !!prepareStringForComparison(value)?.includes(preparedSearchText),
    );
  });
};

export const getContactTypeFormInitialValues = (contactType, presetFamily = null) => {
  const mapping = getDriveMapping(contactType);
  const presetKind = presetFamily?.kind ?? ContactTypeKind.PERSON_AND_COMPANY;

  return {
    name: contactType?.name ?? presetFamily?.code ?? '',
    kind: contactType?.kind ?? presetKind,
    isActive: contactType?.isActive ?? true,
    isDriveEnabled: mapping?.isEnabled ?? true,
    personCode:
      mapping?.personCode ??
      (presetFamily?.kind === ContactTypeKind.PERSON ? presetFamily.code : null),
    companyCode:
      mapping?.companyCode ??
      (presetFamily?.kind === ContactTypeKind.COMPANY ? presetFamily.code : null),
  };
};

const getPayloadKind = (form, contactType) =>
  contactType?.isSystem ? contactType.kind : form.kind;

const getAllowedDriveCodes = (form, kind) => ({
  personCode: getIsPersonCodeAllowed(kind) ? form.personCode || null : null,
  companyCode: getIsCompanyCodeAllowed(kind) ? form.companyCode || null : null,
});

const getHasAllowedDriveCode = (form, kind) => {
  const { personCode, companyCode } = getAllowedDriveCodes(form, kind);

  return !!personCode || !!companyCode;
};

export const getIsDriveCodeMissing = (form, contactType = null) =>
  form.isDriveEnabled && !getHasAllowedDriveCode(form, getPayloadKind(form, contactType));

// The API rejects an enabled mapping with no code, and a stored mapping can only be cleared by
// turning DRIVE off, so an existing mapping left without an allowed code must not be saved.
export const getIsDriveCodeRequired = (form, contactType = null) =>
  !!getDriveMapping(contactType) && getIsDriveCodeMissing(form, contactType);

const getShouldSendDriveMapping = (form, contactType, kind) =>
  !!getDriveMapping(contactType) || !form.isDriveEnabled || getHasAllowedDriveCode(form, kind);

export const getContactTypePayload = (form, contactType = null) => {
  const isSystem = !!contactType?.isSystem;
  const kind = getPayloadKind(form, contactType);

  const mappings = getConditionalArray(getShouldSendDriveMapping(form, contactType, kind), () => [
    {
      provider: ContactTypeExternalProvider.DATA_VERIFY_DRIVE,
      ...getAllowedDriveCodes(form, kind),
      isEnabled: form.isDriveEnabled,
    },
  ]);

  return {
    ...getConditionalObject(!!contactType?.id, () => ({ id: contactType.id })),
    name: isSystem ? contactType.name : form.name.trim(),
    kind,
    isActive: isSystem ? contactType.isActive : form.isActive,
    ...getConditionalObject(!!contactType, () => ({ sortOrder: contactType.sortOrder })),
    mappings,
  };
};
